import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { calculateMortgage, MortgageEstimate } from '../../../core/stats/metrics';
import { BgnPipe, EurPipe } from '../../format.pipes';

@Component({
  selector: 'app-mortgage-calc',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EurPipe, BgnPipe],
  templateUrl: './mortgage-calc.component.html',
  styleUrl: './mortgage-calc.component.scss',
})
export class MortgageCalcComponent {
  /** Median price per sq.m in EUR for the city or selected area. */
  medianPriceEurPerM2 = input.required<number | null | undefined>();
  /** Base benchmark mortgage rate (e.g. from ECB / BNB series ~2.42%). */
  defaultInterestRate = input<number | null | undefined>(2.45);
  cityName = input<string>('града');

  readonly areaPresets = [50, 75, 100];
  readonly downPaymentPresets = [15, 20, 25, 30];
  readonly yearsPresets = [15, 20, 25, 30];

  readonly areaM2 = signal<number>(75);
  readonly downPaymentPct = signal<number>(20);
  readonly years = signal<number>(25);
  readonly interestRate = signal<number>(2.45);

  constructor() {
    effect(() => {
      const def = this.defaultInterestRate();
      if (def !== null && def !== undefined && def > 0) {
        this.interestRate.set(Math.round(def * 100) / 100);
      }
    });
  }

  readonly propertyPrice = computed(() => {
    const ppm2 = this.medianPriceEurPerM2() ?? 0;
    return Math.round(ppm2 * this.areaM2());
  });

  readonly estimate = computed<MortgageEstimate | null>(() => {
    const price = this.propertyPrice();
    return calculateMortgage(price, this.downPaymentPct(), this.interestRate(), this.years());
  });

  setArea(m2: number): void {
    if (m2 > 0) this.areaM2.set(m2);
  }

  setDownPayment(pct: number): void {
    if (pct >= 0 && pct < 100) this.downPaymentPct.set(pct);
  }

  setYears(y: number): void {
    if (y > 0) this.years.set(y);
  }

  updateInterest(val: string): void {
    const num = parseFloat(val);
    if (!isNaN(num) && num >= 0 && num <= 25) {
      this.interestRate.set(num);
    }
  }

  updateArea(val: string): void {
    const num = parseInt(val, 10);
    if (!isNaN(num) && num > 10 && num < 1000) {
      this.areaM2.set(num);
    }
  }
}
